import React, {Component, lazy, Suspense} from 'react';
import PropTypes from 'prop-types';
import {Route, Switch} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';

import {contactFormConfig, privacyFormConfig} from 'lib/shared/form/FormConfig';
import {captureException} from 'lib/client/sentry/sentry';
import {MASTHEAD_URL} from 'lib/shared/config/config';
import LoggedInRoute from 'components/LoggedInRoute/LoggedInRoute';
import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import NotFoundMessage from 'components/NotFoundMessage/NotFoundMessage';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import Notifications from 'components/Notifications/Notifications';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

import styles from './App.module.scss';

// Containers are chunked into separate JS files to make inital page load quicker
const ContactForm = lazy(() => import(/* webpackChunkName: "ContactForm" */ 'containers/ContactForm/ContactForm'));
const CloseAccount = lazy(() => import(/* webpackChunkName: "CloseAccount" */ 'containers/CloseAccount/CloseAccount'));
const ReportContentForm = lazy(() =>
  import(/* webpackChunkName: "ReportContentForm" */ 'containers/ReportContentForm/ReportContentForm')
);

class App extends Component {
  static propTypes = {
    market: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      headerData: undefined,
      footerData: undefined,
    };
  }

  async componentDidMount() {
    const {market} = this.props;

    try {
      const response = await fetch(`${MASTHEAD_URL}?market=${market}`, {credentials: 'include'});

      if (response.ok) {
        const {header, footer} = await response.json();
        this.setState({
          headerData: header,
          footerData: footer,
        });
      } else {
        captureException(`Error when fetching masthead data, got: ${response.status}`);
      }
    } catch (e) {
      captureException(e);
    }
  }

  render() {
    const {headerData, footerData} = this.state;

    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <ScrollToTop>
          <>
            <Header headerData={headerData} />
            <Notifications />
            <div className={styles.mainContainer} role="main">
              <Suspense fallback={<LoadingSpinner />}>
                <Switch>
                  <LoggedInRoute
                    path="/contact-spotify-support/"
                    render={() => <ContactForm formConfig={contactFormConfig} formType="loggedin" />}
                  />
                  <LoggedInRoute
                    allowNotLoggedIn
                    path="/contact-spotify-anonymous/"
                    render={() => <ContactForm formConfig={contactFormConfig} formType="anonymous" />}
                  />
                  <LoggedInRoute
                    allowNotLoggedIn
                    path="/contact-spotify-privacy/"
                    render={() => <ContactForm formConfig={privacyFormConfig} formType="privacy" />}
                  />
                  <Route path="/close-account/" render={() => <CloseAccount />} />
                  <Route path="/report-content/" component={ReportContentForm} />
                  <Route render={() => <NotFoundMessage />} />
                </Switch>
              </Suspense>
            </div>
            <Footer footerData={footerData} />
          </>
        </ScrollToTop>
      </>
    );
  }
}

export default App;
